import React, { useState, useEffect, useRef } from "react";
import { getAuth, signInWithPopup, GoogleAuthProvider, signOut, onAuthStateChanged } from "firebase/auth";
import { loadFirebaseAuth } from "./firebase";
import formatContent from "./formatContent";
import RecipeCard from "./components/RecipeCard";
import { extractRecipeJson } from "./utils/extractRecipeJson";

function App() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [user, setUser] = useState(null);
  const [appName, setAppName] = useState("Loading...");
  const [imageFile, setImageFile] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const hasSpokenInitialMessage = useRef(false);
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const audioRef = useRef(null);  
  const [logoUrl, setLogoUrl] = useState("");
  const chatEndRef = useRef(null);
  const [error, setError] = useState(null);


  const [auth, setAuth] = useState(null);
  const [provider, setProvider] = useState(null);

  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunks = useRef([]);
  
  const [voiceStatus, setVoiceStatus] = useState(""); 
  const [autoVoice, setAutoVoice] = useState(false); // null = unknown
  const [speechToTextEnabled, setSpeechToTextEnabled] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);



  const [loading, setLoading] = useState(false);
  useEffect(() => {
    loadFirebaseAuth().then(({ auth, provider }) => {
      setAuth(auth);
      setProvider(provider);
      onAuthStateChanged(auth, (user) => {
        setUser(user || null);
      });
    });
  }, []);
  
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);

  useEffect(() => {
    if (!imageFile) {
      setImagePreviewUrl(null);
      return;
    }

    const previewUrl = URL.createObjectURL(imageFile);
    setImagePreviewUrl(previewUrl);

    return () => {
      URL.revokeObjectURL(previewUrl); // 🧹 cleanup on unmount or new image
    };
  }, [imageFile]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const newMessages = [...messages, { role: "user", content: input }];
    setMessages(newMessages);
    setInput("");
    setLoading(true);

    try {

      const res = await apiFetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: newMessages }),
      });
      

      const data = await res.json();
      if (data?.reply) {
        setMessages([...newMessages, { role: "assistant", content: data.reply }]);
        
        scrollToBottom();

        if (autoVoice) {
          speakText(data.reply);
        }

      } else {
        setMessages([...newMessages, { role: "assistant", content: "Error: No reply from assistant." }]);
        scrollToBottom();
      }
    } catch (error) {
      console.error("API error:", error);
      setMessages([...newMessages, { role: "assistant", content: "Error contacting assistant." }]);
      scrollToBottom
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async () => {
    //const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      setUser(result.user);
      setUserHasInteracted(true);
    } catch (error) {
      console.error("Login error:", error);
    }
  };

  const handleLogout = () => {
    signOut(auth);
  };

  async function authHeaders(extra = {}) {
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken?.();
    console.log("Auth Token: ", token);
    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...extra,
    };
  }
  async function apiFetch(url, options = {}) {
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken?.();

    const isFormData = options?.body instanceof FormData;
    const inputHeaders = options.headers || {};

    const headers = {
      // Add the token if we have one
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      // Add tenant if you store it
      ...(localStorage.getItem("tenant_id")
        ? { "x-tenant-id": localStorage.getItem("tenant_id") }
        : {}),
      // If NOT FormData and caller didn't set a type, default to JSON
      ...(!isFormData && !("Content-Type" in inputHeaders)
        ? { "Content-Type": "application/json" }
        : {}),
      ...inputHeaders,
    };

  return fetch(url, { ...options, headers });
}

  useEffect(() => {
    const handleInteraction = () => setUserHasInteracted(true);
    window.addEventListener("click", handleInteraction, { once: true });
    window.addEventListener("keydown", handleInteraction, { once: true });
    return () => {
      window.removeEventListener("click", handleInteraction);
      window.removeEventListener("keydown", handleInteraction);
    };
  }, []);

  useEffect(() => {
    if (user && messages.length === 0 && !hasSpokenInitialMessage.current) {
      fetch("/api/admin/load-config")
        .then(res => res.json())
        .then(data => {
          const assistantMessages = (data.STARTUP_MESSAGES || []).map(text => ({
            role: "assistant",
            content: text,
          }));

          setMessages(assistantMessages);
          
          hasSpokenInitialMessage.current = true;
          
          if (data.VOICE_ENABLED && data.AUTO_VOICE && data.STARTUP_VOICE && userHasInteracted) {
            speakText(data.STARTUP_VOICE);
          }
        })
        .catch(() => {
          // fallback if config is missing
          setMessages([
            {
              role: "assistant",
              content: "👋 Hi! I'm your Panda AI Assistant. How can I help you today?",
            },
          ]);
        });
    }
  }, [user, userHasInteracted]);
  

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (configLoaded) return; 
    fetch(`/api/admin/load-config?ts=${Date.now()}`)
      .then((res) => res.json())
      .then((data) => {
        console.log("🔥 Config loaded in production:", data);
        setAppName(data.APP_NAME || "Panda AI Assistant");
        setLogoUrl(data.LOGO_URL || "");

        //Set Voice Settings for Client
        setVoiceEnabled(data.VOICE_ENABLED);
        setAutoVoice(data.AUTO_VOICE === true);
        setSpeechToTextEnabled(data.SPEECH_TO_TEXT === true);

        setConfigLoaded(true);

        // ✅ Apply THEME_COLOR
        const themeColor = data.THEME_COLOR || "#047857";
        document.documentElement.style.setProperty("--theme-color", themeColor);

        if (data.FAVICON_URL) {
          const faviconLink = document.getElementById('dynamic-favicon');
        if (faviconLink) {
          faviconLink.href = data.FAVICON_URL;
        }
      }

      })
      .catch(() => setAppName("Panda AI Assistant"));
  }, []);


  useEffect(() => {
    if (appName) {
      document.title = appName;
    }
  }, [appName]);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const speakText = async (text) => {
    try {
      // Stop any current playback
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
        setIsSpeaking(false);
      }

      const res = await apiFetch("/api/speak", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
    

      if (!res.ok) throw new Error("TTS API failed");

      const blob = await res.blob();
      const audio = new Audio(URL.createObjectURL(blob));
      audioRef.current = audio;

      setIsSpeaking(true); // ✅ Start indicator

      audio.play();

      audio.onended = () => {
        setIsSpeaking(false); // ✅ End indicator
        audioRef.current = null;
      };

      audio.onerror = () => {
        setIsSpeaking(false);
        console.error("TTS playback failed.");
      };
    } catch (err) {
      console.error("TTS error:", err.message);
      setIsSpeaking(false);
    }
  };

  const handleVoiceRecord = async () => {
    if (isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      return;
    }

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaRecorderRef.current = new MediaRecorder(stream, {
      mimeType: "audio/webm"
    });
    audioChunks.current = [];

    mediaRecorderRef.current.ondataavailable = (e) => {
      if (e.data.size > 0) audioChunks.current.push(e.data);
    };

    mediaRecorderRef.current.onstop = async () => {
      try {
        const audioBlob = new Blob(audioChunks.current, { type: "audio/webm" });
        
        console.log("audioBlob size:", audioBlob.size);

        const formData = new FormData();
        formData.append("file", audioBlob, "voice.webm");

        console.log("📤 Sending audio to Whisper...");
        setVoiceStatus("transcribing");
        const res = await apiFetch("/api/transcribe-audio", { method: "POST", body: formData });
        

        //const data = await res.json();
        let data;
        try {
          data = await res.json();
        } catch (err) {
          const fallback = await res.text();
          console.error("⚠️ Whisper error response:", fallback);
          setVoiceStatus("");
          return;
        }
        console.log("📝 Whisper transcription response:", data);

        const spokenText = data.transcription?.trim();
        if (!spokenText) {
          console.warn("⚠️ No transcription received");
          return;
        }

        console.log("🤖 Sending transcription to GPT:", spokenText);

        const newMessages = [...messages, { role: "user", content: spokenText }];
        setMessages(newMessages);
        setLoading(true);

        const chatRes = await apiFetch("/api/chat", {
          method: "POST",
           headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ messages: newMessages }),
        });

        
        const chatData = await chatRes.json();
        console.log("🤖 GPT response:", chatData);

        const reply = chatData?.reply || "(No reply from assistant)";
        setMessages([...newMessages, { role: "assistant", content: reply }]);
        scrollToBottom();
        
        if (voiceEnabled && autoVoice) speakText(reply);
        

      } catch (err) {
        console.error("❌ Voice to GPT error:", err);
      } finally {
        setLoading(false);
        setVoiceStatus(""); // ✅ clear banner

      }
    };


    mediaRecorderRef.current.start();
    setIsRecording(true);
    setVoiceStatus("listening");
  };


  const handleImageUpload = (e) => {
    setImageFile(e.target.files[0]);
  };

  const handleAnalyze = async () => {
    if (!imageFile) return;

    console.log("📦 Uploading image file:", imageFile.name, imageFile.size, imageFile.type);
    setLoadingImage(true);

    try {
      const formData = new FormData();
      formData.append("image", imageFile);
      const res = await apiFetch("/api/analyze-image", {
        method: "POST",
        body: formData,
        headers: {}, // don't set content-type for FormData
      });

      const data = await res.json();
      console.log("📊 Analysis result:", data);

      const { beverageName, recipe } = data;

      const msgText = `🧋 Beverage: **${beverageName || "N/A"}**\n📖 **Recipe:**\n${recipe}`;
      const newMessages = [...messages, { role: "user", content: "Uploaded label image" }];
      
      setMessages([...newMessages, { role: "assistant", content: msgText }]);
      scrollToBottom();

      if (voiceEnabled && autoVoice) {
        speakText(recipe);
      }
    } catch (err) {
      console.error("Image analysis failed:", err.message);
      setMessages([...messages, { role: "assistant", content: "❌ Image analysis failed." }]);
    } finally {
      setLoadingImage(false);
      setImageFile(null);
    }
  };

const renderAssistantMessage = (content) => {
  const recipe = extractRecipeJson(content);
  //console.log("Recipe: ", recipe);
  if (recipe) {
    return <RecipeCard recipe={recipe} />;
  }

  return formatContent(content);
};



if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
        <div className="flex items-center gap-2">
          {logoUrl && <img src={logoUrl} alt="Logo" className="h-10 w-auto" />}
          <h1 className="font-bold text-lg">{appName}</h1>
        </div>
        <button
          onClick={handleSignIn}
          className="bg-theme text-white px-6 py-2 rounded hover:opacity-90 transition-opacity"
        >
          Sign in with Google
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      <header className="bg-theme text-white p-4 flex justify-between items-center">
        <div className="flex items-center gap-2">
          {logoUrl && <img src={logoUrl} alt="Logo" className="h-10 w-auto" />}
          <h1 className="font-bold text-lg">{appName}</h1>
        </div>
        
       {voiceEnabled && (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={autoVoice}
            onChange={() => setAutoVoice(!autoVoice)}
          />
          Auto Voice
        </label>
      )}

        <div className="flex items-center gap-4">
          <span>{user.displayName}</span>
          <button onClick={handleLogout} className="bg-white text-theme px-3 py-1 rounded">
            Logout
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-100">
        
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`p-3 rounded-lg max-w-xl whitespace-pre-wrap flex items-start justify-between gap-2 ${
              msg.role === "user"
                ? "bg-theme-light self-end ml-auto"
                : "bg-white border self-start mr-auto"
            }`}
          >
            <div className="flex-1">
            {msg.role === "assistant"
              ? renderAssistantMessage(msg.content)
              : msg.content}
          </div>
            {msg.role === "assistant" && (
              <button
                onClick={() => speakText(msg.content)}
                className="text-gray-400 hover:text-theme text-lg"
                title="Play Voice"
              >
                🔊
              </button>
            )}
          </div>
        ))}
       

        <div ref={chatEndRef} />

        {voiceStatus && (
          <div className="p-3 rounded-lg max-w-xl bg-white border self-start mr-auto text-gray-500 italic">
            {voiceStatus === "listening" && "🎙 Listening..."}
            {voiceStatus === "transcribing" && "📝 Transcribing..."}
          </div>
        )}

        {loadingImage && (
          <div className="p-3 rounded-lg max-w-xl bg-white border self-start mr-auto text-gray-500 italic">
            🧠 Analyzing image...
          </div>
        )}
        {isSpeaking && (
          <div className="flex items-center gap-3 p-3 rounded-lg max-w-xl bg-white border self-start mr-auto text-gray-500 italic">
            🔊 Speaking...
            <button
              onClick={() => {
                if (audioRef.current) {
                  audioRef.current.pause();
                  audioRef.current.currentTime = 0;
                  audioRef.current = null;
                }
                setIsSpeaking(false);
              }}
              className="text-sm text-red-600 bg-red-100 px-2 py-1 rounded hover:bg-red-200"
            >
              🛑 Stop
            </button>
          </div>
        )}


        {loading && !voiceStatus && (
          <div className="p-3 rounded-lg max-w-xl bg-white border self-start mr-auto text-gray-500 italic">
            Assistant is typing...
          </div>
        )}
      </main>

      {imageFile && (
        <div className="flex items-center gap-4 px-4 py-2 bg-white border-t">
          <img
            src={imagePreviewUrl}
            alt="Preview"
            className="w-20 h-20 object-cover rounded border"
          />
          <button
            onClick={() => setImageFile(null)}
            type="button"
            className="text-sm text-red-600 underline"
          >
            Remove Image
          </button>
        </div>
      )}


      <form onSubmit={handleSend} className="flex flex-wrap items-center gap-2 p-4 border-t bg-white">
        {/* 🖼 Image Upload Icon */}
        <label className="cursor-pointer text-4xl hover:scale-125 transition-transform px-2" title="Upload Image">
          📷
          <input
            type="file"
            accept="image/*"
            capture="environment"  // ✅ Use device camera directly
            onChange={handleImageUpload}
            className="hidden"
          />

        </label>

        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="flex-1 border border-gray-300 rounded px-4 py-2"
          placeholder="Type a message or tap the mic to speak.."
        />

        {voiceEnabled && speechToTextEnabled && (
          <button
            type="button"
            onClick={handleVoiceRecord}
            className={`text-2xl p-3 rounded-full bg-blue-500 text-white transition-all duration-300 ${
              isRecording ? "glow" : ""
            }`}
          >
            {isRecording ? "🛑" : "🎙"}
          </button>
        )}
          


        <button
          type="submit"
          disabled={loading || isRecording}
          className={`px-4 py-2 rounded text-white transition-all duration-300 ${
            loading || isRecording
              ? "bg-gray-300 cursor-not-allowed"
              : "bg-theme hover:opacity-90"
          }`}
        >
          Send
        </button>


        {imageFile && (
          <button
            onClick={handleAnalyze}
            type="button"
            className="bg-indigo-600 text-white px-3 py-2 rounded"
            disabled={loadingImage}
          >
            {loadingImage ? "Analyzing..." : "Send Image"}
          </button>
        )}
      </form>


      
    </div>
  );
}

export default App;